import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataTable } from "@/components/fleet/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { inputCls, primaryBtn } from "@/components/AuthLayout";
import { api, errMsg } from "@/lib/api";

const VFIELDS = [
  ["taxi_no", "Comfort Taxi number", "e.g. 042"],
  ["plate", "Number plate", "e.g. TBA 123"],
  ["make", "Make", "Toyota"],
  ["model", "Model", "Corolla"],
  ["color", "Colour", "Blue"],
  ["year", "Year", "2018"],
];

const AddVehicle = ({ onAdded }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ color: "Blue" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/fleet/vehicles", { ...form, year: form.year ? Number(form.year) : null });
      toast.success("Vehicle registered");
      setOpen(false);
      setForm({ color: "Blue" });
      onAdded();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="add-vehicle-button" className="inline-flex items-center gap-2 rounded-full bg-blue-700 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-700/25 hover:bg-blue-600"><Plus className="h-4 w-4" /> Register vehicle</button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Register a Comfort Taxi vehicle</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3" data-testid="add-vehicle-form">
          {VFIELDS.map(([k, l, ph]) => (
            <label key={k} className="block text-xs font-semibold text-slate-500">{l}
              <input data-testid={`vehicle-${k}-input`} required={k !== "year"} type={k === "year" ? "number" : "text"} placeholder={ph} className={`${inputCls} mt-1`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
            </label>
          ))}
          <button data-testid="add-vehicle-submit" className={`${primaryBtn} col-span-2 mt-2`}>Save vehicle</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const VehiclesPanel = () => {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/fleet/vehicles").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => {
    load();
  }, []);
  const setStatus = async (id, status) => {
    await api.put(`/fleet/vehicles/${id}`, { status });
    toast.success("Vehicle updated");
    load();
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-end"><AddVehicle onAdded={load} /></div>
      <DataTable
        testId="vehicles-table"
        rows={rows}
        empty="No vehicles registered yet."
        cols={[
          ["Taxi #", (r) => <b className="font-mono-num">#{r.taxi_no}</b>],
          ["Plate", (r) => r.plate],
          ["Vehicle", (r) => `${r.color} ${r.make} ${r.model}${r.year ? ` (${r.year})` : ""}`],
          ["Drivers", (r) => (r.drivers.length ? r.drivers.join(", ") : "—")],
          ["Status", (r) => <StatusBadge status={r.status} />],
          ["", (r) => (
            <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
              <SelectTrigger data-testid={`vehicle-status-select-${r.id}`} className="h-8 w-36 rounded-lg text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="maintenance">Maintenance</SelectItem>
              </SelectContent>
            </Select>
          )],
        ]}
      />
    </div>
  );
};
